import { API_PREFIX } from "../auth/apiClient";

// ---------------------------------------------------------------------------
// Client gap telemetry
//
// The server cannot see a tab that has no connection, so the tab reports its
// own gaps: how often they happen and how long they last. Volume stays near
// zero. A gap is reported once when it ends, if a user could see it. A gap
// that reaches two minutes is reported once more while it is still open. A
// tracker sends at most ten reports, and the provider makes one tracker per
// mount. Nothing is sent per retry, per poll, or per state change.
// ---------------------------------------------------------------------------

export type GapPhase = "recovered" | "long";
export type GapRecovery = "reconnect" | "restart" | "start" | "unknown";

export interface GapReport {
  readonly phase: GapPhase;
  readonly recovery: GapRecovery;
  readonly durationMs: number;
}

/** Equals the "Live updates are off" banner delay: only gaps a user can see are reported. */
export const GAP_REPORT_FLOOR_MS = 5_000;
/** One "still down" report per gap, when it reaches this age. */
export const GAP_LONG_MS = 120_000;
/** Longest duration a report can carry, so laptop sleep cannot distort percentiles. */
export const GAP_MAX_REPORT_MS = 1_800_000;
/** Most reports one tracker sends. Both phases count. */
export const GAP_REPORT_BUDGET = 10;

export interface GapTracker {
  /** The connection is not live. Opens a gap if none is open. */
  lost(): void;
  /** The connection is live. Closes the open gap, if any, and reports it if a user could see it. */
  recovered(recovery: Exclude<GapRecovery, "unknown">): void;
  /** Cancels the timer and stops all reporting. */
  dispose(): void;
}

export interface GapTrackerOptions {
  readonly report: (report: GapReport) => void;
  /** Clock in ms. Defaults to `Date.now`. */
  readonly now?: () => number;
  readonly floorMs?: number;
  readonly longMs?: number;
  readonly maxReports?: number;
}

export function createGapTracker(options: GapTrackerOptions): GapTracker {
  const {
    report,
    now = () => Date.now(),
    floorMs = GAP_REPORT_FLOOR_MS,
    longMs = GAP_LONG_MS,
    maxReports = GAP_REPORT_BUDGET,
  } = options;

  let openedAt: number | null = null;
  let longTimer: ReturnType<typeof setTimeout> | null = null;
  let sent = 0;
  let disposed = false;

  const clearLongTimer = (): void => {
    if (longTimer === null) return;
    clearTimeout(longTimer);
    longTimer = null;
  };

  const durationSince = (start: number): number =>
    Math.round(Math.min(GAP_MAX_REPORT_MS, Math.max(0, now() - start)));

  const send = (next: GapReport): void => {
    if (disposed || sent >= maxReports) return;
    sent += 1;
    try {
      report(next);
    } catch {
      // A bad sink must never break the connection.
    }
  };

  return {
    lost() {
      if (disposed || openedAt !== null) return;
      const start = now();
      openedAt = start;
      longTimer = setTimeout(() => {
        longTimer = null;
        send({ phase: "long", recovery: "unknown", durationMs: durationSince(start) });
      }, longMs);
    },
    recovered(recovery) {
      if (disposed || openedAt === null) return;
      const durationMs = durationSince(openedAt);
      openedAt = null;
      clearLongTimer();
      if (durationMs >= floorMs) send({ phase: "recovered", recovery, durationMs });
    },
    dispose() {
      disposed = true;
      openedAt = null;
      clearLongTimer();
    },
  };
}

// ---------------------------------------------------------------------------
// Gap reporter
//
// One fire-and-forget POST per report. It never throws, never logs, and never
// retries: telemetry is best effort and must add no load while the system is
// unhealthy. Every failure is ignored, including a 404 from a server that does
// not have the route yet and a failing token call. `keepalive` lets a report
// that is sent as the page closes still go out.
// ---------------------------------------------------------------------------

const CLIENT_GAPS_URL = `${API_PREFIX}/realtime/client-gaps`;

export interface GapReporterOptions {
  readonly getToken: () => Promise<string>;
  readonly fetchImpl?: typeof fetch;
}

export function createGapReporter(options: GapReporterOptions): (report: GapReport) => void {
  const post = async ({ phase, recovery, durationMs }: GapReport): Promise<void> => {
    const token = await options.getToken();
    await (options.fetchImpl ?? fetch)(CLIENT_GAPS_URL, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ phase, recovery, durationMs }),
      keepalive: true,
    });
  };

  return (report) => {
    void post(report).catch(() => undefined);
  };
}
